/**
 * Reine UI-Helfer für den Users-&-Roles-Tab (Slice 1, read-only). Bewusst OHNE
 * Server-Imports (kein `db`/`permissions` → kein DB-Treiber im Browser-Bundle);
 * Permission-Katalog/Effective kommen zur Laufzeit aus der API.
 */

export type PermissionGroup = "view" | "action";
export const PERMISSION_GROUP_ORDER: PermissionGroup[] = ["view", "action"];

export const PERMISSION_GROUP_LABEL: Record<PermissionGroup, string> = {
  view: "view.*",
  action: "action.*",
};

export interface PermissionMeta {
  id: string;
  group: PermissionGroup;
  label: string;
}

/** Aufschlüsselung der effektiven Rechte (Shape der API `user_effective_permissions`). */
export interface EffectiveLike {
  legacyAdmin: boolean;
  roleDerived: string[];
  directAllow: string[];
  directDeny: string[];
  effective: string[];
}

/** Woher kommt ein Recht? (Anzeige-Badge) */
export type PermSource = "legacy-admin" | "deny" | "allow" | "role" | "none";

export function permissionSource(id: string, eff: EffectiveLike): PermSource {
  if (eff.legacyAdmin) return "legacy-admin";
  if (eff.directDeny.includes(id)) return "deny";
  if (eff.directAllow.includes(id)) return "allow";
  if (eff.roleDerived.includes(id)) return "role";
  return "none";
}

export interface PermRow {
  id: string;
  label: string;
  group: PermissionGroup;
  granted: boolean;
  source: PermSource;
}

/** Eine Zeile je Katalog-Permission mit granted-Flag + Quelle. */
export function effectivePermissionRows(catalog: PermissionMeta[], eff: EffectiveLike): PermRow[] {
  const effSet = new Set(eff.effective);
  return catalog.map((p) => ({
    id: p.id,
    label: p.label,
    group: p.group,
    granted: effSet.has(p.id),
    source: permissionSource(p.id, eff),
  }));
}

/** Beliebige {group}-Zeilen in stabiler Gruppen-Reihenfolge bündeln. */
export function groupByPrefix<T extends { group: PermissionGroup }>(rows: T[]): { group: PermissionGroup; items: T[] }[] {
  return PERMISSION_GROUP_ORDER.map((group) => ({ group, items: rows.filter((r) => r.group === group) }));
}

/* ------------------------------------------------ Rollen-Permission-Matrix (Slice 2b) */

/** Systemrollen: server-seitig immutable (Spiegel von src/roles/manage.ts). */
export const SYSTEM_ROLE_NAMES = new Set<string>(["super_admin", "admin"]);

export function isSystemRoleName(name: string): boolean {
  return SYSTEM_ROLE_NAMES.has(name);
}

export interface MatrixRow {
  id: string;
  label: string;
  group: PermissionGroup;
  checked: boolean;
}

/** Eine Checkbox-Zeile je Katalog-Permission; `checked` aus der aktuellen Auswahl. */
export function roleMatrixRows(catalog: PermissionMeta[], currentPermissions: Iterable<string>): MatrixRow[] {
  const cur = new Set(currentPermissions);
  return catalog.map((p) => ({ id: p.id, label: p.label, group: p.group, checked: cur.has(p.id) }));
}

/**
 * added/removed-Diff (UI-Spiegel des Backends): sortiert, dedupliziert. Nur für
 * Anzeige/Save-Enable — autoritativ ist immer das Backend.
 */
export function diffPermissions(current: Iterable<string>, next: Iterable<string>): { added: string[]; removed: string[] } {
  const cur = new Set(current);
  const nxt = new Set(next);
  const added = [...nxt].filter((p) => !cur.has(p)).sort();
  const removed = [...cur].filter((p) => !nxt.has(p)).sort();
  return { added, removed };
}

/** Hat die Auswahl sich ggü. dem geladenen Stand geändert? */
export function hasPermissionChanges(current: Iterable<string>, next: Iterable<string>): boolean {
  const d = diffPermissions(current, next);
  return d.added.length > 0 || d.removed.length > 0;
}

export interface EditableRoleLike {
  name: string;
  /** Server-seitiges Flag aus `roles_get`; `false` = Systemrolle. */
  editable?: boolean;
}

/**
 * Darf die Rolle im UI editiert werden? Nur wenn der Nutzer `action.manage_roles`
 * hat UND die Rolle keine Systemrolle ist. Reines Convenience-Gating — der
 * Schreibpfad ist zusätzlich server-seitig geschützt (Slice 2a).
 */
export function canEditRolePermissions(role: EditableRoleLike | null | undefined, canManageRoles: boolean): boolean {
  if (!role || !canManageRoles) return false;
  if (role.editable === false) return false;
  return !isSystemRoleName(role.name);
}

/* -------------------------------------------- User-Rollen-Zuweisung (Slice 3b) */

/** Eine Rolle, wie sie aus `user_roles_get` kommt (assigned/available). */
export interface RoleLike {
  id: number;
  name: string;
  label: string;
  editable?: boolean;
}

export interface AssignmentRow {
  id: number;
  name: string;
  label: string;
  checked: boolean;
}

/**
 * Editierbare Zuweisungs-Zeilen aus den zuweisbaren Rollen. Systemrollen werden
 * hier IMMER ausgeschlossen (server-seitig ebenfalls geblockt) — sie sind nie
 * Teil der Save-Payload. `checked` aus der aktuellen Auswahl.
 */
export function roleAssignmentRows(available: RoleLike[], selectedIds: Iterable<number>): AssignmentRow[] {
  const sel = new Set(selectedIds);
  return available
    .filter((r) => !isSystemRoleName(r.name) && r.editable !== false)
    .map((r) => ({ id: r.id, name: r.name, label: r.label, checked: sel.has(r.id) }));
}

/** IDs der zugewiesenen Nicht-System-Rollen — die editierbare Initialauswahl. */
export function assignedNonSystemRoleIds(assigned: RoleLike[]): number[] {
  return assigned.filter((r) => !isSystemRoleName(r.name)).map((r) => r.id);
}

/** Zugewiesene Systemrollen (sichtbar, aber nie editier-/entziehbar über diese UI). */
export function assignedSystemRoles(assigned: RoleLike[]): RoleLike[] {
  return assigned.filter((r) => isSystemRoleName(r.name));
}

/** added/removed-Diff zweier Role-ID-Mengen (UI-Spiegel des Backends; sortiert). */
export function diffRoleIds(current: Iterable<number>, next: Iterable<number>): { added: number[]; removed: number[] } {
  const cur = new Set(current);
  const nxt = new Set(next);
  const added = [...new Set(next)].filter((id) => !cur.has(id)).sort((a, b) => a - b);
  const removed = [...cur].filter((id) => !nxt.has(id)).sort((a, b) => a - b);
  return { added, removed };
}

/** Hat sich die Rollen-Auswahl ggü. dem geladenen Stand geändert? */
export function hasRoleChanges(current: Iterable<number>, next: Iterable<number>): boolean {
  const d = diffRoleIds(current, next);
  return d.added.length > 0 || d.removed.length > 0;
}

/**
 * Darf der Nutzer User-Rollen zuweisen? Gate = AND: BEIDE `action.manage_users`
 * UND `action.manage_roles` (Spiegel des server-seitigen AND-Gates). Reines
 * Convenience-Gating — der Schreibpfad ist zusätzlich server-seitig geschützt.
 */
export function canEditUserRoles(canManageUsers: boolean, canManageRoles: boolean): boolean {
  return canManageUsers && canManageRoles;
}

/**
 * Darf der Nutzer ein User-Passwort zurücksetzen? Gate = `action.manage_users`
 * (Spiegel des server-seitigen Gatings). Reines Convenience-Gating — der
 * Schreibpfad ist zusätzlich server-seitig geschützt (Slice 15a).
 */
export function canResetPassword(canManageUsers: boolean): boolean {
  return canManageUsers;
}

/** Darf der Nutzer neue User anlegen? Gate = `action.manage_users` (Spiegel des Backends). */
export function canCreateUser(canManageUsers: boolean): boolean {
  return canManageUsers;
}

/**
 * Baut die Create-Payload — bewusst NUR `{ user, name }`. Garantiert, dass kein
 * `password`/`role`/`servers`/`permissions` mitgeschickt wird (role/servers setzt
 * der Server hart auf `user`/leer). `name` getrimmt; leer bleibt leer (Server
 * defaulted dann auf den Login).
 */
export function createUserPayload(user: string, name: string): { user: string; name: string } {
  return { user: user.trim(), name: name.trim() };
}
